"use client";
import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import heroImage from '@/app/assets/images/nest_web_marketing_designer_landing_page_hero_image.png';
import { AxiosProgressEvent } from 'axios';
import { Design, UploadState, UploadProgress } from '@/app/lib/types/stypes';
import { useRouter } from 'next/navigation';
import { uploadDesignFile } from '@/app/lib/api/fileUpload';
import { processFile } from "./handleZipUpload";
import SupplierHeroSection from '../SupplierHeroSection';
import useGetRawProductsUploadProgress from '@/app/lib/hooks/useGetRawProductsUploadProgress';
import { useUserSession } from '@/app/lib/context/UserSessionContext';

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const FileUpload: React.FC = () => {
  const { sessionInfo } = useUserSession()
  const [trackingId, setTrackingId] = useState('')
  const { data: uploadProgressData, isError: uploadProgressIsError } = useGetRawProductsUploadProgress(trackingId)
  // const isUploadInProgress = window.localStorage.getItem('uploadRawProductDataTrackingId')
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const router = useRouter();
  const [progress, setProgress] = useState<UploadProgress>({
    percentage: 2,
    loaded: 0,
    total: 0,
  });
  useEffect(() => {
    //effect for polling to get the upload progress
    if (uploadProgressData) {
      setProgress({
        percentage: uploadProgressData.percentage > 2 ? uploadProgressData.percentage : 2,
        loaded: uploadProgressData.processedProducts,
        total: uploadProgressData.totalProducts,
      });
      if (uploadProgressData.percentage === 100) {
        window.localStorage.removeItem(`uploadRawProductDataTrackingId${sessionInfo?.session?.vendorId}`);
        setUploadState('success');
        // router.push(`/supplier/products`);
        window.location.href = `/ai/supplier/products`;
      }
    }
  }, [uploadProgressData])
  useEffect(() => {
    const storedId = window.localStorage.getItem(`uploadRawProductDataTrackingId${sessionInfo?.session?.vendorId}`);
    if (storedId) {
      setTrackingId(storedId);
      setUploadState('uploading');
    }
  }, []);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    // fileInputRef.current?.click();
    router.push('/tools/supplier-product-feed-management');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadState('uploading');
    setError(null);
    try {
      //@ts-ignore
      const apiFormData = await processFile(file, sessionInfo);

      if (!apiFormData) throw new Error('Failed to fetch to process the file');

      // setUploadState('uploading');
      // setError(null);

      const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        //not using this fn
        // if (progressEvent.total) {
        //   setProgress({
        //     percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
        //     loaded: progressEvent.loaded,
        //     total: progressEvent.total,
        //   });
        // }
      };

      const result: any = await uploadDesignFile(apiFormData, onUploadProgress);
      if (result?.trackingId) {
        setTrackingId(result.trackingId);
        window.localStorage.setItem(`uploadRawProductDataTrackingId${sessionInfo?.session?.vendorId}`, result.trackingId);
      }
      // setUploadState('success');
      // router.push(`/uploadedProducts`);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('File upload failed');
      setUploadState('error');
    }
  };

  const handleCancel = () => {
    resetState();
    window.localStorage.removeItem(`uploadRawProductDataTrackingId${sessionInfo?.session?.vendorId}`);
  };

  const resetState = () => {
    setUploadState('idle');
    setProgress({ percentage: 0, loaded: 0, total: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  if (uploadProgressIsError) return <div>Error fetching upload progress</div>
  return (
    <div>
      {uploadState === 'idle' && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileChange(e)}
            accept=".xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.zip"
            aria-hidden="true"
            className="hidden"
          />
          <SupplierHeroSection handleUploadClick={handleUploadClick} />
        </>
      )}

      {uploadState === 'uploading' && (
        <div className="text-center p-8 flex flex-col justify-center relative w-full h-screen overflow-hidden items-center">
          <Image
            src={heroImage}
            alt="Hero Background"
            fill={true}
            priority={true}
            placeholder="blur"
            className="object-cover opacity-60 blur-sm"
          />
          <div className='relative'>
            <h2 className="text-[42px] font-semibold mb-6">We're processing your files...</h2>
            <h3 className='text-[24px] text-[#52575C] mb-1'>This will take just a few minutes</h3>
            <div className="mb-8">
              <div className="h-[60px] bg-gray-200 rounded-md overflow-hidden mb-2">
                <div
                  className="h-full bg-[#FE6526] transition-all duration-300 animate-pulse"
                  style={{ width: `${progress.percentage}%` }}
                  role="progressbar"
                  aria-valuenow={progress.percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
              <span className="text-sm text-gray-500">
                {/* {progress.percentage}% ({formatBytes(progress.loaded)}/{formatBytes(progress.total)}) */}
                {uploadProgressData && (
                  <span className='text-lg'>
                    {uploadProgressData?.processedProducts} of {uploadProgressData?.totalProducts} completed
                    <span className="inline-block animate-pulse text-3xl">...</span>
                  </span>
                )}
              </span>
            </div>
            <button
              className="bg-orange-500  text-white font-medium py-2 px-4 rounded-md transition-colors cursor-pointer"
              onClick={handleCancel}
            >
              Cancel Upload
            </button>
          </div>
        </div>
      )}

      {uploadState === 'error' && (
        <div className="text-center p-8">
          <div className="flex items-center justify-center gap-2 text-red-600 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
          <button
            className="bg-[#FE6526] text-white font-semibold py-2 px-4 rounded-md transition-colors"
            onClick={resetState}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;